import React, { useEffect, useState, useCallback } from "react";
import CollapsibleCard from "../components/ui/CollapsibleCard";
import { toParisTime, fmtAnswerTime } from "../utils/date";
import ReassocModal from "../components/ReassocModal";
import { apiFetch } from "../utils/api";
import Spinner from "../components/ui/Spinner";

function formatDuration(startStr, endStr) {
  if (!startStr || !endStr) return "—";
  const diffMs = new Date(startStr) - new Date(endStr);
  const abs = Math.abs(diffMs);
  if (abs <= 0) return "—";
  const totalSec = Math.round(abs / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return min > 0 ? `${min}m ${sec}s` : `${sec}s`;
}

function flattenForms(events) {
  const rows = [];
  for (const [eventId, eventData] of Object.entries(events)) {
    const groupStartTime = eventData.startTime || null;
    const groupEndTime = eventData.endTime || null;
    for (const form of eventData.forms || []) {
      // form.startTime/endTime (bornes précises de l'essai, associate_for_site_v4) priment
      // sur les bornes de l'événement Syrinix groupé (ancien format / overrides manuels).
      const startTime = form.startTime || groupStartTime;
      const endTime = form.endTime || groupEndTime;
      rows.push({ ...form, startTime, endTime, matched_event: eventId, unassociated: false });
    }
  }
  rows.sort((a, b) => {
    const na = parseInt(a.form_id, 10);
    const nb = parseInt(b.form_id, 10);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return String(a.form_id).localeCompare(String(b.form_id));
  });
  return rows;
}

function sortRows(rows, config) {
  const assoc = rows.filter(r => !r.unassociated);
  const unassoc = rows.filter(r => r.unassociated);

  if (config) {
    const { key, dir } = config;
    const mul = dir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      let va, vb;
      if (key === "form_id") {
        va = parseInt(a.form_id, 10) || 0;
        vb = parseInt(b.form_id, 10) || 0;
      } else if (key === "answer_time" || key === "photo_time") {
        va = a[key] ? new Date(a[key].replace(" ", "T")).getTime() : -Infinity;
        vb = b[key] ? new Date(b[key].replace(" ", "T")).getTime() : -Infinity;
      } else if (key === "startTime" || key === "endTime") {
        va = a[key] ? new Date(a[key]).getTime() : -Infinity;
        vb = b[key] ? new Date(b[key]).getTime() : -Infinity;
      } else if (key === "duration") {
        va = a.startTime && a.endTime ? Math.abs(new Date(a.endTime) - new Date(a.startTime)) : -1;
        vb = b.startTime && b.endTime ? Math.abs(new Date(b.endTime) - new Date(b.startTime)) : -1;
      } else {
        return 0;
      }
      if (va < vb) return -mul;
      if (va > vb) return mul;
      return 0;
    };
    assoc.sort(cmp);
    unassoc.sort(cmp);
  }

  return [...assoc, ...unassoc];
}

function SortableHeader({ children, sortKey, config, onSort }) {
  const isActive = config?.key === sortKey;
  const icon = isActive ? (config.dir === "asc" ? " ↑" : " ↓") : " ↕";
  return (
    <th
      className="px-3 py-2 text-left border-b font-medium cursor-pointer select-none hover:bg-gray-200 whitespace-nowrap"
      onClick={() => onSort(sortKey)}
    >
      {children}
      <span className={isActive ? "text-violet-500 ml-0.5" : "text-gray-300 ml-0.5"}>{icon}</span>
    </th>
  );
}

function PiBadge({ value }) {
  if (!value) return <span className="text-gray-400">—</span>;
  const color = value === "Conforme"
    ? "bg-green-100 text-green-800 border-green-300"
    : value === "Non-Conforme"
    ? "bg-red-100 text-red-800 border-red-300"
    : "bg-gray-100 text-gray-500 border-gray-300";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${color}`}>
      {value}
    </span>
  );
}

function SiteSection({
  siteId,
  siteName,
  events,
  noMatchList,
  meeList,
  annotatedForms,
  invalidForms,
  sortConfig,
  onSort,
  triageLoading,
  onTriage,
  onReload,
}) {
  const [expanded, setExpanded] = useState(false);
  const [showMee, setShowMee] = useState(false);
  const [reassocTarget, setReassocTarget] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetFeedback, setResetFeedback] = useState(null); // null | 'ok' | 'err'
  const [iaSiteResetLoading, setIaSiteResetLoading] = useState(false);
  const [iaSiteResetFeedback, setIaSiteResetFeedback] = useState(null); // null | 'ok' | 'err'
  const [iaFormReset, setIaFormReset] = useState({}); // { [form_id]: 'loading' | 'ok' | 'err' }
  const [volumeByForm, setVolumeByForm] = useState({}); // { [form_id]: { status: 'loading'|'ok'|'err', value?, cached? } }
  const [csvExportLoading, setCsvExportLoading] = useState(false);
  const [csvExportFeedback, setCsvExportFeedback] = useState(null); // null | 'ok' | 'err'
  const [csvExportProgress, setCsvExportProgress] = useState(null); // { current, total } | null

  const handleResetTriage = async (e) => {
    e.stopPropagation();
    setResetLoading(true);
    setResetFeedback(null);
    try {
      await apiFetch(`/streamlit/triage-cache/${siteId}`, { method: "DELETE" });
      setResetFeedback("ok");
    } catch {
      setResetFeedback("err");
    } finally {
      setResetLoading(false);
      setTimeout(() => setResetFeedback(null), 3000);
    }
  };

  const handleResetIaSite = async (e) => {
    e.stopPropagation();
    setIaSiteResetLoading(true);
    setIaSiteResetFeedback(null);
    try {
      await apiFetch(`/streamlit/ai-cache/${siteId}`, { method: "DELETE" });
      setIaSiteResetFeedback("ok");
    } catch {
      setIaSiteResetFeedback("err");
    } finally {
      setIaSiteResetLoading(false);
      setTimeout(() => setIaSiteResetFeedback(null), 3000);
    }
  };

  const handleResetIaForm = async (formId) => {
    setIaFormReset(prev => ({ ...prev, [formId]: "loading" }));
    try {
      await apiFetch(`/streamlit/ai-cache/${siteId}/${formId}`, { method: "DELETE" });
      setIaFormReset(prev => ({ ...prev, [formId]: "ok" }));
    } catch {
      setIaFormReset(prev => ({ ...prev, [formId]: "err" }));
    } finally {
      setTimeout(() => setIaFormReset(prev => ({ ...prev, [formId]: undefined })), 3000);
    }
  };

  const handleFetchVolume = async (formId) => {
    setVolumeByForm(prev => ({ ...prev, [formId]: { status: "loading" } }));
    try {
      const data = await apiFetch(`/streamlit/volume/${siteId}/${formId}`);
      setVolumeByForm(prev => ({ ...prev, [formId]: { status: "ok", value: data.volume_l, cached: data.cached } }));
    } catch {
      setVolumeByForm(prev => ({ ...prev, [formId]: { status: "err" } }));
    }
  };

  const handleExportTestCsv = async (e) => {
    e.stopPropagation();
    setCsvExportLoading(true);
    setCsvExportFeedback(null);
    setCsvExportProgress(null);
    try {
      await apiFetch(`/api/essais/${siteId}/test-csv/start`, { method: "POST" });

      // Poll jusqu'à la fin (ou une erreur) — sans ça, aucun moyen de
      // distinguer un traitement long (fetch Syrinix séquentiel, tout
      // l'historique du site) d'un blocage.
      let status;
      do {
        await new Promise(resolve => setTimeout(resolve, 1500));
        status = await apiFetch(`/api/essais/${siteId}/test-csv/status`);
        setCsvExportProgress(status.total > 0 ? { current: status.current, total: status.total } : null);
        if (status.error) throw new Error(status.error);
      } while (status.running);

      const r = await fetch(`/api/essais/${siteId}/test-csv/download`);
      if (!r.ok) {
        // Téléchargement de fichier : ne peut pas passer par apiFetch (qui fait
        // r.json()), mais doit quand même déclencher le retour à l'écran de
        // connexion sur 401 comme apiFetch (cf. remontée Benoît 12/08/2026).
        if (r.status === 401) window.dispatchEvent(new Event("v3:auth-expired"));
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || r.statusText);
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `essais_test_site_${siteId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setCsvExportFeedback("ok");
    } catch {
      setCsvExportFeedback("err");
    } finally {
      setCsvExportLoading(false);
      setCsvExportProgress(null);
      setTimeout(() => setCsvExportFeedback(null), 3000);
    }
  };

  const assocRows = flattenForms(events || {});
  const unassocRows = (noMatchList || []).map(f => ({
    ...f,
    startTime: null,
    endTime: null,
    unassociated: true,
  }));
  const baseRows = [...assocRows, ...unassocRows];
  const rows = sortRows(baseRows, sortConfig);

  const totalForms     = rows.length;
  const annotatedCount = rows.filter(r => annotatedForms.has(r.form_id)).length;
  const invalidCount   = rows.filter(r => invalidForms.has(r.form_id)).length;
  const unassocCount   = unassocRows.length;
  const meeCount       = (meeList || []).length;

  return (
    <div className="border rounded shadow-sm">
      {/* Site header */}
      <div className="flex items-center gap-2 p-3 bg-gray-50 border-b">
        <button
          onClick={() => setExpanded(p => !p)}
          className="font-semibold text-left flex-1 flex items-center gap-2 text-sm"
        >
          <span className="text-gray-400">{expanded ? "▲" : "▼"}</span>
          <span>Site {siteId}{siteName ? ` — ${siteName}` : ""}</span>
          <span className="text-xs font-normal text-gray-500">
            {totalForms} formulaire{totalForms !== 1 ? "s" : ""}
            {annotatedCount > 0 && ` · ${annotatedCount} annoté${annotatedCount !== 1 ? "s" : ""}`}
            {invalidCount > 0 && ` · ${invalidCount} invalide${invalidCount !== 1 ? "s" : ""}`}
            {unassocCount > 0 && ` · ${unassocCount} sans assoc.`}
            {meeCount > 0 && ` · ${meeCount} MEE`}
          </span>
        </button>
        <button
          type="button"
          disabled={triageLoading}
          onClick={(e) => { e.stopPropagation(); onTriage(siteId); }}
          className="px-3 py-1 text-xs rounded border border-slate-600 text-slate-600 hover:bg-slate-50 disabled:opacity-50 whitespace-nowrap"
        >
          {triageLoading ? "Génération..." : "Rapport de triage"}
        </button>
        <button
          type="button"
          disabled={resetLoading}
          onClick={handleResetTriage}
          title="Supprimer le rapport de triage sauvegardé pour forcer un recalcul"
          className={`px-3 py-1 text-xs rounded border whitespace-nowrap transition-colors disabled:opacity-50 ${
            resetFeedback === "ok"
              ? "border-green-500 text-green-600 bg-green-50"
              : resetFeedback === "err"
              ? "border-red-400 text-red-600 bg-red-50"
              : "border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600"
          }`}
        >
          {resetLoading ? "..." : resetFeedback === "ok" ? "✓ Réinitialisé" : resetFeedback === "err" ? "Erreur" : "↺ Réinitialiser"}
        </button>
        <button
          type="button"
          disabled={iaSiteResetLoading}
          onClick={handleResetIaSite}
          title="Invalider les prédictions IA de tous les formulaires du site (après re-entraînement du modèle)"
          className={`px-3 py-1 text-xs rounded border whitespace-nowrap transition-colors disabled:opacity-50 ${
            iaSiteResetFeedback === "ok"
              ? "border-green-500 text-green-600 bg-green-50"
              : iaSiteResetFeedback === "err"
              ? "border-red-400 text-red-600 bg-red-50"
              : "border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600"
          }`}
        >
          {iaSiteResetLoading ? "..." : iaSiteResetFeedback === "ok" ? "✓ Réinitialisé" : iaSiteResetFeedback === "err" ? "Erreur" : "↺ Reset IA (site)"}
        </button>
        <button
          type="button"
          disabled={csvExportLoading}
          onClick={handleExportTestCsv}
          title="Générer un CSV de test regroupant tous les essais détectés (nouvel algorithme), à réinjecter dans Syrinix en une fois pour vérifier visuellement les bornes détectées"
          className={`px-3 py-1 text-xs rounded border whitespace-nowrap transition-colors disabled:opacity-50 ${
            csvExportFeedback === "ok"
              ? "border-green-500 text-green-600 bg-green-50"
              : csvExportFeedback === "err"
              ? "border-red-400 text-red-600 bg-red-50"
              : "border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600"
          }`}
        >
          {csvExportLoading
            ? (csvExportProgress ? `${csvExportProgress.current}/${csvExportProgress.total} événements...` : "Démarrage...")
            : csvExportFeedback === "ok" ? "✓ Téléchargé"
            : csvExportFeedback === "err" ? "Erreur"
            : "⬇ CSV de test (Syrinix)"}
        </button>
      </div>

      {expanded && (
        <div>
          {/* Form table */}
          {rows.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-100 text-gray-600 uppercase tracking-wide">
                    <SortableHeader sortKey="form_id" config={sortConfig} onSort={onSort}>
                      Formulaire Kizeo
                    </SortableHeader>
                    <th className="px-3 py-2 text-left border-b font-medium whitespace-nowrap">Type</th>
                    <th className="px-3 py-2 text-left border-b font-medium whitespace-nowrap">PI en eau</th>
                    <SortableHeader sortKey="answer_time" config={sortConfig} onSort={onSort}>
                      Heure envoi
                    </SortableHeader>
                    <SortableHeader sortKey="photo_time" config={sortConfig} onSort={onSort}>
                      Heure photo
                    </SortableHeader>
                    <SortableHeader sortKey="startTime" config={sortConfig} onSort={onSort}>
                      Début Syrinix
                    </SortableHeader>
                    <SortableHeader sortKey="endTime" config={sortConfig} onSort={onSort}>
                      Fin Syrinix
                    </SortableHeader>
                    <SortableHeader sortKey="duration" config={sortConfig} onSort={onSort}>
                      Durée
                    </SortableHeader>
                    <th className="px-3 py-2 text-left border-b font-medium whitespace-nowrap">Volume</th>
                    <th className="px-3 py-2 text-left border-b font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const isAnnotated    = annotatedForms.has(row.form_id);
                    const isInvalid      = invalidForms.has(row.form_id);
                    const isUnassociated = row.unassociated === true;

                    const rowBg = isInvalid
                      ? "bg-red-50"
                      : isUnassociated
                      ? "bg-yellow-50"
                      : isAnnotated
                      ? "bg-blue-50"
                      : "bg-white";

                    return (
                      <tr key={row.form_id} className={`${rowBg} border-b hover:brightness-95`}>
                        {/* ID + badges */}
                        <td className="px-3 py-2 font-mono font-medium">
                          <span className="inline-flex items-center gap-1.5">
                            <button
                              type="button"
                              onClick={() => setReassocTarget({ formId: row.form_id, siteId, currentEventId: row.matched_event })}
                              className="text-gray-300 hover:text-blue-500 transition-colors shrink-0"
                              title="Modifier l'association"
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                              </svg>
                            </button>
                            {row.form_id}
                            {row.manual && (
                              <span className="font-sans text-[9px] font-semibold px-1 rounded bg-blue-100 text-blue-600 border border-blue-200">M</span>
                            )}
                          </span>
                          {isInvalid && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700 border border-red-300">
                              invalide
                            </span>
                          )}
                          {isUnassociated && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-yellow-100 text-yellow-700 border border-yellow-300">
                              sans assoc.
                            </span>
                          )}
                          {isAnnotated && !isInvalid && !isUnassociated && (
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 border border-blue-300">
                              annoté
                            </span>
                          )}
                        </td>

                        {/* Type de contrôle */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          {row.type_de_controle ? (
                            <span className="text-gray-600">{row.type_de_controle}</span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>

                        {/* PI en eau */}
                        <td className="px-3 py-2">
                          <PiBadge value={row.pi_en_eau1} />
                        </td>

                        {/* Heure envoi (answer_time) — ancre v3 */}
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap font-medium">
                          {fmtAnswerTime(row.answer_time)}
                        </td>

                        {/* Heure photo */}
                        <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                          {fmtAnswerTime(row.photo_time)}
                        </td>

                        {/* Début Syrinix */}
                        <td className={`px-3 py-2 whitespace-nowrap ${isUnassociated ? "text-gray-300" : "text-gray-700"}`}>
                          {toParisTime(row.startTime)}
                        </td>

                        {/* Fin Syrinix */}
                        <td className={`px-3 py-2 whitespace-nowrap ${isUnassociated ? "text-gray-300" : "text-gray-700"}`}>
                          {toParisTime(row.endTime)}
                        </td>

                        {/* Durée */}
                        <td className={`px-3 py-2 whitespace-nowrap ${isUnassociated ? "text-gray-300" : "text-gray-700"}`}>
                          {formatDuration(row.startTime, row.endTime)}
                        </td>

                        {/* Volume (litres) — chargement à la demande, pas au chargement de la page */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          {isUnassociated ? (
                            <span className="text-gray-300">—</span>
                          ) : volumeByForm[row.form_id]?.status === "ok" ? (
                            <span className="text-gray-700" title={volumeByForm[row.form_id].cached ? "Depuis le cache" : "Calculé à l'instant (Syrinix)"}>
                              {volumeByForm[row.form_id].value != null ? `${volumeByForm[row.form_id].value} L` : "—"}
                            </span>
                          ) : volumeByForm[row.form_id]?.status === "loading" ? (
                            <span className="inline-flex items-center gap-1 text-gray-400 text-xs"><Spinner /> ...</span>
                          ) : volumeByForm[row.form_id]?.status === "err" ? (
                            <button
                              type="button"
                              onClick={() => handleFetchVolume(row.form_id)}
                              className="text-xs text-red-500 hover:underline"
                            >
                              Erreur, réessayer
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleFetchVolume(row.form_id)}
                              className="btn btn-sm btn-ghost"
                            >
                              Voir volume
                            </button>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="px-3 py-2">
                          {!isUnassociated && (
                            <div className="flex items-center gap-1 flex-wrap">
                              <a
                                href={`/streamlit/export_measure_v3/${siteId}/${row.form_id}`}
                                className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-gray-400 text-gray-600 hover:bg-gray-50"
                                download
                              >
                                CSV cut
                              </a>
                              <a
                                href={`/streamlit/export_measure_v3_full/${siteId}/${row.form_id}`}
                                className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-gray-400 text-gray-600 hover:bg-gray-50"
                                download
                                title="Export brut, sans découpage — pour comparer avec CSV cut"
                              >
                                CSV full
                              </a>
                              <button
                                type="button"
                                onClick={() => {
                                  // Chemins relatifs (pas d'origin) : app.py les route via BACKEND_URL
                                  // (réseau Docker interne), sans passer par nginx — évite le Basic
                                  // Auth de l'environnement dev et le contournement P0-Y sur prod.
                                  const rawUrl  = `/streamlit/export_measure_v3/${siteId}/${row.form_id}`;
                                  const normUrl = `/streamlit/export_measure_normalized_v3/${siteId}/${row.form_id}`;
                                  const baseVis = new URL("/visualisation/", window.location.origin);
                                  baseVis.searchParams.set("csv_raw",  rawUrl);
                                  baseVis.searchParams.set("csv_norm", normUrl);
                                  baseVis.searchParams.set("form_id",  row.form_id);
                                  baseVis.searchParams.set("site_id",  siteId);
                                  window.open(baseVis.toString(), "_blank", "noopener,noreferrer");
                                }}
                                className="inline-flex items-center px-2 py-0.5 text-xs rounded border border-violet-500 text-violet-600 hover:bg-violet-50"
                              >
                                Streamlit
                              </button>
                              <button
                                type="button"
                                disabled={iaFormReset[row.form_id] === "loading"}
                                onClick={() => handleResetIaForm(row.form_id)}
                                title="Invalider la prédiction IA de ce formulaire (force une re-prédiction)"
                                className={`inline-flex items-center px-2 py-0.5 text-xs rounded border whitespace-nowrap transition-colors disabled:opacity-50 ${
                                  iaFormReset[row.form_id] === "ok"
                                    ? "border-green-500 text-green-600 bg-green-50"
                                    : iaFormReset[row.form_id] === "err"
                                    ? "border-red-400 text-red-600 bg-red-50"
                                    : "border-gray-400 text-gray-500 hover:bg-gray-50"
                                }`}
                              >
                                {iaFormReset[row.form_id] === "loading" ? "..." : iaFormReset[row.form_id] === "ok" ? "✓" : iaFormReset[row.form_id] === "err" ? "Erreur" : "↺ IA"}
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="px-4 py-3 text-xs text-gray-400 italic">Aucun formulaire pour ce site.</p>
          )}

          {/* MEE section */}
          {meeCount > 0 && (
            <div className="border-t">
              <button
                onClick={() => setShowMee(p => !p)}
                className="w-full flex items-center gap-2 px-4 py-2 text-xs text-amber-700 bg-amber-50 hover:bg-amber-100 transition-colors"
              >
                <span>{showMee ? "▲" : "▼"}</span>
                <span className="font-medium">Mises en eau ({meeCount})</span>
              </button>
              {showMee && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-amber-50 text-amber-800 uppercase tracking-wide">
                        <th className="px-3 py-2 text-left border-b font-medium">Formulaire Kizeo</th>
                        <th className="px-3 py-2 text-left border-b font-medium">Heure envoi</th>
                        <th className="px-3 py-2 text-left border-b font-medium">Heure photo</th>
                        <th className="px-3 py-2 text-left border-b font-medium">Code SDIS66</th>
                      </tr>
                    </thead>
                    <tbody>
                      {meeList.map((mee) => (
                        <tr key={mee.form_id} className="bg-amber-50/50 border-b hover:brightness-95">
                          <td className="px-3 py-2 font-mono">
                            {mee.form_id}
                            <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-300">
                              MEE
                            </span>
                          </td>
                          <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                            {fmtAnswerTime(mee.answer_time)}
                          </td>
                          <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                            {fmtAnswerTime(mee.photo_time)}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {mee.code_sdis66 || "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {reassocTarget && (
        <ReassocModal
          formId={reassocTarget.formId}
          siteId={reassocTarget.siteId}
          currentEventId={reassocTarget.currentEventId}
          onClose={() => setReassocTarget(null)}
          onSaved={() => { setReassocTarget(null); onReload?.(); }}
        />
      )}
    </div>
  );
}

export default function Traitement() {
  const [data, setData]               = useState(null);
  const [noMatchData, setNoMatchData] = useState({});
  const [meeData, setMeeData]         = useState({});
  const [campaigns, setCampaigns]     = useState([]);
  const [siteToCampaign, setSiteToCampaign] = useState({});
  const [siteNames, setSiteNames]     = useState({});
  const [annotatedForms, setAnnotatedForms] = useState(new Set());
  const [invalidForms, setInvalidForms]     = useState(new Set());
  const [sortConfigs, setSortConfigs] = useState({});
  const [expandedCampaigns, setExpandedCampaigns] = useState({});
  const [triageLoading, setTriageLoading] = useState({});
  const [cleaningPhases, setCleaningPhases] = useState(false);
  const [cleanResult, setCleanResult] = useState(null);
  const [error, setError]             = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [assocResult, noMatchResult, meeResult, campaignResult, pumpsResult, annotResult, invalidResult] =
        await Promise.allSettled([
          apiFetch("/api/match/associated-table"),
          apiFetch("/api/match/no-match-table"),
          apiFetch("/api/match/mee-table"),
          apiFetch("/api/match/site-campaign-map"),
          apiFetch("/api/radar/pumps"),
          apiFetch("/api/ai/annotated-forms"),
          apiFetch("/api/streamlit/invalid_forms"),
        ]);

      // apiFetch renvoie déjà le JSON parsé (pas une Response) et déclenche
      // "v3:auth-expired" sur 401 avant de rejeter — donc un 401 ici rebascule
      // sur l'écran de connexion au lieu de laisser Traitement continuer avec
      // des données partielles (l'ancien fetch() brut ne passait pas par cette
      // vérification, cf. remontée Benoît 12/08/2026).
      const ok = (r) => r.status === "fulfilled";

      if (!ok(assocResult)) {
        setError(assocResult.reason?.message || "API associated-table injoignable");
        setData({});
        return;
      }

      const assocData    = assocResult.value;
      const noMatchRaw   = ok(noMatchResult)   ? noMatchResult.value   : {};
      const meeRaw       = ok(meeResult)       ? meeResult.value       : {};
      const campaignRaw  = ok(campaignResult)  ? campaignResult.value  : { campaigns: [], site_to_campaign: {} };
      const pumpsRaw     = ok(pumpsResult)     ? pumpsResult.value     : { sites: {} };
      const annotRaw     = ok(annotResult)     ? annotResult.value     : { annotated_forms: [] };
      const invalidRaw   = ok(invalidResult)   ? invalidResult.value   : [];

      const names = pumpsRaw.sites || {};
      setData(assocData);
      setNoMatchData(noMatchRaw);
      setMeeData(meeRaw);
      setCampaigns(campaignRaw.campaigns || []);
      setSiteToCampaign(campaignRaw.site_to_campaign || {});
      setSiteNames(Object.fromEntries(Object.entries(names).map(([id, info]) => [id, info.name])));
      setAnnotatedForms(new Set(annotRaw.annotated_forms || []));
      setInvalidForms(new Set(Array.isArray(invalidRaw) ? invalidRaw : []));
    } catch (err) {
      console.error("Erreur chargement Traitement", err);
      setData({});
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  if (!data) {
    return <div className="card p-6 text-sm text-gray-400">Chargement...</div>;
  }

  // Collect all site IDs from data + noMatch
  const allSiteIds = new Set([...Object.keys(data), ...Object.keys(noMatchData), ...Object.keys(meeData)]);

  if (allSiteIds.size === 0 && !error) {
    return (
      <div className="space-y-4">
        <GlobalToolbar cleaningPhases={cleaningPhases} setCleaningPhases={setCleaningPhases} cleanResult={cleanResult} setCleanResult={setCleanResult} />
        <div className="card p-4 bg-yellow-50 border-yellow-200 text-sm text-yellow-800">
          <p className="font-medium mb-1">Aucune donnée disponible</p>
          <p>Lancez d'abord le pipeline depuis l'onglet <strong>Récupération des données</strong> pour générer les associations v3.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <GlobalToolbar cleaningPhases={cleaningPhases} setCleaningPhases={setCleaningPhases} cleanResult={cleanResult} setCleanResult={setCleanResult} />
        <div className="card p-4 bg-red-50 border-red-200">
          <p className="text-red-800 text-sm">❌ {error}</p>
        </div>
      </div>
    );
  }

  // Build campaign → sites mapping. Sites not in any campaign go to "_unknown"
  const campaignSites = {};
  for (const c of campaigns) {
    const siteList = c.sites.filter(sid => allSiteIds.has(sid));
    if (siteList.length > 0) {
      campaignSites[c.id] = { name: c.name, sites: siteList };
    }
  }

  // Sites with no campaign assignment
  const unmappedSites = [...allSiteIds].filter(sid => !siteToCampaign[sid]).sort();

  const makeOnSort = (siteId) => (key) => {
    setSortConfigs((prev) => {
      const cur = prev[siteId];
      if (!cur || cur.key !== key) return { ...prev, [siteId]: { key, dir: "asc" } };
      if (cur.dir === "asc") return { ...prev, [siteId]: { key, dir: "desc" } };
      const next = { ...prev };
      delete next[siteId];
      return next;
    });
  };

  const handleTriage = (siteId) => {
    // Nouveau tableau simplifié (demande Jordan, 27/07/2026) — remplace le
    // rapport HTML historique.
    setTriageLoading(prev => ({ ...prev, [siteId]: true }));
    window.open(`/rapport-triage?site_id=${siteId}`, "_blank", "noopener,noreferrer");
    setTimeout(() => setTriageLoading(prev => ({ ...prev, [siteId]: false })), 2000);
  };

  const renderSite = (siteId) => (
    <SiteSection
      key={siteId}
      siteId={siteId}
      siteName={siteNames[siteId]}
      events={data[siteId] || {}}
      noMatchList={noMatchData[siteId] || []}
      meeList={meeData[siteId] || []}
      annotatedForms={annotatedForms}
      invalidForms={invalidForms}
      sortConfig={sortConfigs[siteId]}
      onSort={makeOnSort(siteId)}
      triageLoading={!!triageLoading[siteId]}
      onTriage={handleTriage}
      onReload={loadData}
    />
  );

  const campaignEntries = campaigns.filter(c => campaignSites[c.id]);

  return (
    <div className="space-y-4">
      <GlobalToolbar
        cleaningPhases={cleaningPhases}
        setCleaningPhases={setCleaningPhases}
        cleanResult={cleanResult}
        setCleanResult={setCleanResult}
      />

      {/* Campagnes */}
      {campaignEntries.map(c => {
        const sites = campaignSites[c.id].sites;
        return (
          <CollapsibleCard
            key={c.id}
            icon="📋"
            title={c.name || `Campagne ${c.id}`}
            badge={`${sites.length} site${sites.length !== 1 ? "s" : ""}`}
            defaultOpen={false}
            onOpenChange={(open) => setExpandedCampaigns(prev => ({ ...prev, [c.id]: open }))}
          >
            <div className="p-3 space-y-3">
              {sites.map(siteId => renderSite(siteId))}
            </div>
          </CollapsibleCard>
        );
      })}

      {/* Sites sans campagne */}
      {unmappedSites.length > 0 && (
        <CollapsibleCard
          icon="❓"
          title="Sites sans campagne assignée"
          badge={unmappedSites.length}
          defaultOpen={false}
        >
          <div className="p-3 space-y-3">
            {unmappedSites.map(siteId => renderSite(siteId))}
          </div>
        </CollapsibleCard>
      )}
    </div>
  );
}

function GlobalToolbar({ cleaningPhases, setCleaningPhases, cleanResult, setCleanResult }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex justify-end items-center gap-2">
        <button
          type="button"
          disabled={cleaningPhases}
          onClick={async () => {
            if (!window.confirm("Supprimer tous les fichiers heuristiques (*phase*) dans data/annotations/ ?")) return;
            setCleaningPhases(true);
            setCleanResult(null);
            try {
              setCleanResult(await apiFetch("/api/streamlit/clean-heuristic-phases", { method: "DELETE" }));
            } catch (e) {
              setCleanResult({ error: String(e) });
            } finally {
              setCleaningPhases(false);
            }
          }}
          className="btn btn-sm border border-orange-500 text-orange-600 hover:bg-orange-50"
        >
          {cleaningPhases ? "Nettoyage..." : "Nettoyer données heuristiques"}
        </button>
        <button
          type="button"
          onClick={() => window.open("/visualisation/", "_blank", "noopener,noreferrer")}
          className="btn btn-sm btn-primary"
        >
          Ouvrir Streamlit
        </button>
      </div>
      {cleanResult && (
        <div className={`text-sm rounded px-3 py-2 border ${cleanResult.error ? "feedback-error" : "feedback-success"}`}>
          {cleanResult.error
            ? `Erreur : ${cleanResult.error}`
            : `${cleanResult.deleted_count} fichier(s) supprimé(s)${cleanResult.errors?.length ? ` — ${cleanResult.errors.length} erreur(s)` : ""}`
          }
        </div>
      )}
    </div>
  );
}
